/**
 * The For Business order matrix.
 *
 * A trade order is several colours at once, each broken across sizes, so every
 * colour the buyer touches keeps its own row and rows accumulate. Horizon's
 * colour picker is left in place above — it swaps the hero photograph, which is
 * worth keeping — but changing a variant makes Horizon re-render and morph the
 * section, which destroys any row this script added to the DOM.
 *
 * So the DOM is not where the order lives. It lives in `orders` below, keyed by
 * product, and is rewritten into the table on every connect. That is what makes
 * a colour change non-destructive: the morph throws the table away and we put
 * it straight back.
 *
 * On submit, each colour becomes one cart line carrying that colour's sizes as
 * line item properties. The first line goes through Horizon's own form so the
 * artwork upload, the optimistic cart and the error handling all keep working;
 * any further colours are posted alongside it.
 */

/** @type {Map<string, Map<string, Map<string, number>>>} product id -> colour -> size -> qty */
const orders = new Map();

/**
 * Horizon's own components listen for this to refresh the cart icon and drawer.
 * Matched by name rather than imported from '@shopify/events' so that a failure
 * to reach Shopify's CDN cannot stop the matrix itself from working.
 */
const CART_UPDATED = 'shopify:cart:lines-update';

const QUOTE_LABEL = 'Add %d to quote basket';
const EMPTY_LABEL = 'Add to quote basket';

class MoeMarkSizeQuantity extends HTMLElement {
  connectedCallback() {
    this.form = this.closest('form');
    if (!this.form) return;

    this.productId = this.dataset.productId;
    this.hasColour = this.dataset.hasColour === 'true';
    this.selectedColour = this.readSelectedColour();

    const data = this.querySelector('[data-moemark-variants]');
    if (!data) return;

    try {
      const parsed = JSON.parse(data.textContent);
      this.sizes = parsed.sizes || [];
      this.byColour = groupByColour(parsed.variants || []);
    } catch {
      // A malformed payload must not take the whole form down; the
      // server-rendered row still works on its own.
      return;
    }

    this.rowsEl = this.querySelector('[data-moemark-rows]');
    this.totalEl = this.querySelector('[data-moemark-size-total]');

    if (!orders.has(this.productId)) orders.set(this.productId, new Map());
    this.order = orders.get(this.productId);

    // The selected colour always has a row, even empty, so there is somewhere
    // to type the moment the buyer picks it. A product with no colour option
    // keys its single row by the empty string — same map, one row, no picker;
    // without this seed, render() would wipe the server-rendered fallback and
    // rebuild nothing, leaving the matrix permanently empty.
    const rowColour = this.hasColour ? this.selectedColour : '';
    if ((rowColour || !this.hasColour) && !this.order.has(rowColour)) {
      this.order.set(rowColour, new Map());
    }

    this.onInput = this.onInput.bind(this);
    this.onClick = this.onClick.bind(this);
    this.onSubmit = this.onSubmit.bind(this);
    this.onVariantChange = this.onVariantChange.bind(this);
    this.onCartUpdated = this.onCartUpdated.bind(this);

    this.addEventListener('input', this.onInput);
    this.addEventListener('click', this.onClick);

    // Not on the form: Horizon re-renders and morphs this section on every
    // variant change, replacing the form and the colour radios while leaving
    // this element alone, so a listener bound to either would be thrown away
    // the first time someone picked a colour.
    //
    // And on `window`, not `document`, because Horizon delegates its declarative
    // `on:submit` from a document-level capture listener registered when
    // component.js loads — long before this one. Two capture listeners on the
    // same node run in registration order, so on `document` we would be second:
    // Horizon would post the form as it stands, with whatever variant the colour
    // picker last selected, before we ever got to rewrite it. Window capture
    // runs a phase earlier, which is the only place stopImmediatePropagation
    // can still hold the event back.
    window.addEventListener('submit', this.onSubmit, { capture: true });
    document.addEventListener('change', this.onVariantChange);
    document.addEventListener(CART_UPDATED, this.onCartUpdated);

    this.render();
  }

  disconnectedCallback() {
    this.removeEventListener('input', this.onInput);
    this.removeEventListener('click', this.onClick);
    window.removeEventListener('submit', this.onSubmit, { capture: true });
    document.removeEventListener('change', this.onVariantChange);
    document.removeEventListener(CART_UPDATED, this.onCartUpdated);
  }

  /**
   * The colour the picker is currently showing.
   *
   * Read from the checked radio rather than from the data attribute, because
   * the morph leaves this element — attribute and all — exactly as it was. The
   * attribute is only the value the page first loaded with.
   */
  readSelectedColour() {
    if (!this.hasColour) return '';

    for (const radio of document.querySelectorAll('input[data-option-name]')) {
      const name = (radio.dataset.optionName || '').toLowerCase();
      if (radio.checked && (name.indexOf('colo') !== -1)) return radio.value;
    }

    return this.dataset.selectedColour || '';
  }

  onVariantChange(event) {
    const radio = event.target.closest('input[data-option-name]');
    if (!radio) return;

    const name = (radio.dataset.optionName || '').toLowerCase();
    if (name.indexOf('colo') === -1) return;

    const colour = this.readSelectedColour();
    if (!colour || colour === this.selectedColour) return;

    this.selectedColour = colour;
    if (!this.order.has(colour)) this.order.set(colour, new Map());

    // Horizon replaces the buy buttons on a variant change, so the label and
    // the quantity it wrote are gone; render puts both back.
    this.render();
  }

  /**
   * Empty the matrix once the order is safely in the basket.
   *
   * Leaving the numbers up would mean a second press of Add silently doubles
   * everything — and on a quote, quantities are the whole content. Waiting for
   * the cart to confirm rather than clearing on submit means a failed add
   * leaves the buyer's work where they can still see it.
   */
  onCartUpdated() {
    if (!this.pendingClear) return;
    this.pendingClear = false;

    this.order.clear();
    const rowColour = this.hasColour ? this.selectedColour : '';
    if (rowColour || !this.hasColour) this.order.set(rowColour, new Map());
    this.render();
  }

  onInput(event) {
    const input = event.target.closest('[data-moemark-size]');
    if (!input) return;

    const colour = input.closest('[data-colour]')?.dataset.colour ?? '';
    const size = input.dataset.size;
    const quantity = Math.max(0, Math.floor(Number(input.value) || 0));

    const sizes = this.order.get(colour) ?? new Map();
    if (quantity > 0) sizes.set(size, quantity);
    else sizes.delete(size);
    this.order.set(colour, sizes);

    this.updateTotals();
  }

  onClick(event) {
    const remove = event.target.closest('[data-moemark-remove]');
    if (!remove) return;

    event.preventDefault();
    const colour = remove.closest('[data-colour]')?.dataset.colour ?? '';
    // The selected colour keeps an empty row rather than vanishing, because it
    // is the row the buyer is looking at.
    if (colour === this.selectedColour) this.order.set(colour, new Map());
    else this.order.delete(colour);

    this.render();
  }

  /** Colours with something in them, plus the one currently selected. */
  get liveColours() {
    const colours = [];
    for (const [colour, sizes] of this.order) {
      if (sizes.size > 0 || colour === this.selectedColour) colours.push(colour);
    }
    return colours;
  }

  /**
   * One cart line per size, not per colour.
   *
   * Grouping a colour onto one line read better in the basket, and was wrong:
   * every catalogue variant denies overselling, so Shopify validates the line's
   * quantity against the stock of the single variant it carries. Five garments
   * spread across five sizes were checked as five of whichever size happened to
   * be first — and a colour whose opening size was short took the whole order
   * down with it.
   *
   * A line per size is also simply true. The variant is the size, so the basket
   * states the order instead of hiding it, and each quantity is checked against
   * the stock it actually draws on.
   *
   * @returns {Array<{colour: string, size: string, quantity: number, variantId: number}>}
   */
  get lines() {
    const lines = [];

    for (const colour of this.liveColours) {
      const sizes = this.order.get(colour);
      if (!sizes || sizes.size === 0) continue;

      for (const size of this.sizes) {
        const count = sizes.get(size);
        if (!count) continue;

        const variant = this.byColour.get(colour)?.get(size);
        if (!variant) continue;

        // Anything beyond what the warehouse holds today is a back-order: it is
        // ordered in rather than refused, and saying so on the line is the whole
        // point — Moe confirms the lead time when he prices the job.
        const backorder = variant.stock >= 0 ? Math.max(0, count - variant.stock) : 0;

        lines.push({ colour, size, quantity: count, variantId: variant.id, backorder });
      }
    }

    return lines;
  }

  /** The same order collapsed to one entry per colour, for the summary line. */
  get colourTotals() {
    const totals = new Map();
    for (const line of this.lines) {
      totals.set(line.colour, (totals.get(line.colour) ?? 0) + line.quantity);
    }
    return totals;
  }

  render() {
    if (!this.rowsEl) return;

    this.rowsEl.textContent = '';
    for (const colour of this.liveColours) {
      this.rowsEl.appendChild(this.buildRow(colour));
    }

    this.updateTotals();
  }

  buildRow(colour) {
    const sizes = this.order.get(colour) ?? new Map();
    const available = this.byColour.get(colour) ?? new Map();
    const isSelected = colour === this.selectedColour;

    const row = document.createElement('tr');
    row.className = 'moemark-matrix__row';
    if (isSelected) row.classList.add('is-selected');
    row.dataset.colour = colour;

    const head = document.createElement('th');
    head.scope = 'row';
    head.className = 'moemark-matrix__colour';
    head.textContent = colour;
    row.appendChild(head);

    for (const size of this.sizes) {
      const cell = document.createElement('td');
      cell.className = 'moemark-matrix__cell';
      const variant = available.get(size);

      if (variant) {
        const wanted = sizes.get(size) ?? 0;
        const short = variant.stock >= 0 && wanted > variant.stock;

        const input = document.createElement('input');
        input.className = 'moemark-size__input';
        if (short) input.classList.add('moemark-size__input--backorder');
        input.type = 'number';
        input.inputMode = 'numeric';
        input.min = '0';
        input.step = '1';
        input.placeholder = '0';
        input.value = wanted ? String(wanted) : '';
        input.dataset.moemarkSize = '';
        input.dataset.size = size;
        input.dataset.variantId = String(variant.id);
        input.setAttribute(
          'aria-label',
          colour + ' ' + size + (short ? ' — ' + (wanted - variant.stock) + ' of these on back-order' : '')
        );
        if (short) input.title = variant.stock + ' in stock today, ' + (wanted - variant.stock) + ' to follow';
        // Only the selected colour's row carries form field names, so that with
        // scripting off Horizon still posts exactly one coherent line.
        if (isSelected) input.name = 'properties[' + size + ']';
        cell.appendChild(input);
      } else {
        const absent = document.createElement('span');
        absent.className = 'moemark-matrix__absent';
        absent.textContent = '–';
        absent.setAttribute('aria-label', 'Not made in this size');
        cell.appendChild(absent);
      }

      row.appendChild(cell);
    }

    const total = document.createElement('td');
    total.className = 'moemark-matrix__total';
    total.dataset.moemarkRowTotal = '';
    row.appendChild(total);

    const removeCell = document.createElement('td');
    removeCell.className = 'moemark-matrix__remove';
    if (sizes.size > 0) {
      const button = document.createElement('button');
      button.type = 'button';
      button.dataset.moemarkRemove = '';
      button.className = 'moemark-matrix__remove-button';
      button.setAttribute('aria-label', 'Clear ' + colour);
      button.textContent = '×';
      removeCell.appendChild(button);
    }
    row.appendChild(removeCell);

    return row;
  }

  /**
   * Mark the cells whose quantity has outrun the shelf.
   *
   * Done here rather than only when a row is built, because rows are built on
   * colour change and quantities are typed in between: without this the border
   * appeared a colour-change late, long after the decision was made.
   */
  refreshBackorderMarks() {
    for (const input of this.querySelectorAll('[data-moemark-size]')) {
      const colour = input.closest('[data-colour]')?.dataset.colour ?? '';
      const variant = this.byColour.get(colour)?.get(input.dataset.size);
      const wanted = Math.max(0, Math.floor(Number(input.value) || 0));
      const short = variant && variant.stock >= 0 && wanted > variant.stock;

      input.classList.toggle('moemark-size__input--backorder', Boolean(short));

      if (short) {
        input.title = variant.stock + ' in stock today, ' + (wanted - variant.stock) + ' to follow';
        input.setAttribute(
          'aria-label',
          colour + ' ' + input.dataset.size + ' — ' + (wanted - variant.stock) + ' of these on back-order'
        );
      } else {
        input.removeAttribute('title');
        input.setAttribute('aria-label', colour + ' ' + input.dataset.size);
      }
    }
  }

  updateTotals() {
    this.refreshBackorderMarks();

    for (const row of this.querySelectorAll('[data-colour]')) {
      const sizes = this.order.get(row.dataset.colour) ?? new Map();
      let sum = 0;
      for (const count of sizes.values()) sum += count;
      const cell = row.querySelector('[data-moemark-row-total]');
      if (cell) cell.textContent = sum > 0 ? String(sum) : '';
      row.classList.toggle('has-quantity', sum > 0);
    }

    const lines = this.lines;
    const total = lines.reduce((sum, line) => sum + line.quantity, 0);

    this.writeSummary(lines, total);
    this.writeQuantity(total);
    this.writeButton(total);
  }

  writeSummary(lines, total) {
    if (!this.totalEl) return;

    if (total === 0) {
      this.totalEl.textContent = 'No sizes chosen yet';
      this.totalEl.classList.remove('is-set');
      return;
    }

    const totals = this.colourTotals;
    const parts = [];
    for (const [colour, count] of totals) parts.push(colour + ' ' + count);
    const spread = totals.size === 1 ? '' : ' across ' + totals.size + ' colours';

    const backorder = lines.reduce((sum, line) => sum + line.backorder, 0);
    // Said before the order is placed rather than discovered afterwards. The
    // count is the honest part: how many pieces are not on the shelf today.
    const tail = backorder > 0 ? ' · ' + backorder + ' on back-order' : '';

    this.totalEl.textContent =
      total + ' garment' + (total === 1 ? '' : 's') + spread + ' — ' + parts.join(' · ') + tail;
    this.totalEl.classList.toggle('has-backorder', backorder > 0);
    this.totalEl.classList.add('is-set');
  }

  get liveForm() {
    return this.closest('form');
  }

  writeQuantity(total) {
    const input = this.liveForm?.querySelector('input[name="quantity"]');
    if (!input) return;

    // Horizon disables the box when the selected variant is out of stock. A
    // quote is not a sale and PenCarrie restock daily, so a size being short
    // this afternoon must not stop someone asking what the job would cost.
    input.disabled = false;
    input.value = String(Math.max(total, 1));
  }

  writeButton(total) {
    const button = this.liveForm?.querySelector('button[name="add"]');
    if (!button) return;

    const label = Array.from(button.childNodes).find(
      (node) => node.nodeType === Node.TEXT_NODE && node.textContent.trim() !== ''
    );
    if (label) label.textContent = total > 0 ? QUOTE_LABEL.replace('%d', String(total)) : EMPTY_LABEL;
    button.disabled = total === 0;
  }

  /**
   * Horizon's form can carry one line. When the order is one colour that is all
   * it needs to be, and this steps out of the way entirely. When there are
   * more, the extra colours are posted first and the form is then re-submitted
   * carrying the first — so whatever happens, Horizon's own pipeline is what
   * finishes the job and updates the cart.
   */
  onSubmit(event) {
    if (!(event.target instanceof HTMLFormElement) || !event.target.contains(this)) return;
    this.form = event.target;

    // Strictly one-shot. This flag marks the re-submit we asked for ourselves,
    // and clearing it on the way past is the whole point: left standing, every
    // later submit would skip the rewrite below and post whatever stale ids and
    // properties happened to be sitting in the form — which is exactly how a
    // three-colour order once became one line of fifty of the wrong thing.
    if (this.passingThrough) {
      this.passingThrough = false;
      return;
    }

    const lines = this.lines;
    if (lines.length === 0) {
      event.preventDefault();
      event.stopImmediatePropagation();
      return;
    }

    // Always, before anything is allowed through: the form carries whichever
    // variant the colour picker last selected and whichever inputs were last
    // named, neither of which need be the line we mean to post.
    this.rewriteFormFor(lines[0]);
    this.pendingClear = true;
    if (lines.length === 1) return;

    event.preventDefault();
    event.stopImmediatePropagation();

    const extras = lines.slice(1).map((line) => ({
      id: line.variantId,
      quantity: line.quantity,
      properties: Object.assign({ _quote: 'true' }, this.sharedProperties(), backorderProperty(line)),
    }));

    const button = this.form.querySelector('button[name="add"]');
    if (button) button.disabled = true;

    const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

    fetch(root + 'cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items: extras }),
    })
      .then((response) => {
        if (!response.ok) throw new Error('extra colours rejected');
        return response.json();
      })
      .then(() => {
        // Hand back to Horizon for the first line, artwork and all.
        this.passingThrough = true;
        if (button) button.disabled = false;
        this.form.requestSubmit(button || undefined);
      })
      .catch(() => {
        if (button) button.disabled = false;
        if (this.totalEl) {
          this.totalEl.textContent = 'Could not add the extra colours — please try again.';
          this.totalEl.classList.add('is-set');
        }
      });
  }

  /** Everything on the form that is not a size, so extra lines match the first. */
  sharedProperties() {
    const shared = {};
    // form.elements, not querySelectorAll: the print choices can sit in the
    // band under the product, outside the form and tied to it by their form
    // attribute, and only the form's own list includes those.
    for (const field of this.form.elements) {
      if (!field.name || !field.name.startsWith('properties[')) continue;
      if (field.closest('moemark-size-quantity')) continue;
      if (field.type === 'file') continue;
      // A disabled field is excluded from the form's own FormData post, so it
      // must not ride onto the extra fetched lines either - a position set to
      // None disables its details fields for exactly this reason.
      if (field.disabled) continue;
      // Radios all carry a value whether or not they are chosen; only the
      // checked one is real. Without this, the last option in the DOM would
      // ride onto every extra line regardless of what was picked.
      if ((field.type === 'radio' || field.type === 'checkbox') && !field.checked) continue;
      const name = field.name.slice('properties['.length, -1);
      if (field.value) shared[name] = field.value;
    }
    return shared;
  }

  /** Point Horizon's form at one colour's line. */
  rewriteFormFor(line) {
    const id = this.form.querySelector('input[name="id"]');
    if (id) id.value = String(line.variantId);

    const quantity = this.form.querySelector('input[name="quantity"]');
    if (quantity) {
      quantity.disabled = false;
      quantity.value = String(line.quantity);
    }

    // The variant now carries the size, so no size ever travels as a property.
    // The names only exist for the scriptless path, and must not ride along on a
    // post this script is driving.
    for (const field of this.querySelectorAll('[data-moemark-size]')) {
      field.removeAttribute('name');
    }

    // Horizon's form has no field for this, so one is made and cleaned up after.
    this.querySelector('[data-moemark-backorder-field]')?.remove();
    const backorder = backorderProperty(line)['Back-order'];
    if (backorder) {
      const field = document.createElement('input');
      field.type = 'hidden';
      field.name = 'properties[Back-order]';
      field.value = backorder;
      field.dataset.moemarkBackorderField = '';
      this.appendChild(field);
    }
  }
}

/** [colour, size, id, stock] rows -> colour -> size -> {id, stock} */
function groupByColour(rows) {
  const byColour = new Map();
  for (const row of rows) {
    const colour = row[0];
    const size = row[1];
    if (!byColour.has(colour)) byColour.set(colour, new Map());
    // stock of -1 means the variant is untracked, so nothing is knowable about
    // it and nothing will be called a back-order.
    byColour.get(colour).set(size, { id: row[2], stock: row[3] });
  }
  return byColour;
}

/** The line item property that tells Moe how much of a line has to be ordered in. */
function backorderProperty(line) {
  if (!line.backorder) return {};
  return { 'Back-order': line.backorder + ' of ' + line.quantity + ' to follow' };
}

function cssEscape(value) {
  return window.CSS && CSS.escape ? CSS.escape(value) : String(value).replace(/["\\]/g, '\\$&');
}

if (!customElements.get('moemark-size-quantity')) {
  customElements.define('moemark-size-quantity', MoeMarkSizeQuantity);
}
